import React, { forwardRef, useCallback } from 'react';
import type { ButtonProps } from '@atlaskit/button';
import { IconButton, type IconButtonProps, type IconProp } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import TableColumnsDistributeIcon from '@atlaskit/icon/core/table-columns-distribute';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Pressable, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

type Props = {
	isLoading?: boolean;
	isInBetweenColumnPickerEnabled?: boolean;
	isSelected?: boolean;
	customIcon?: IconProp;
	onClick?: ButtonProps['onClick'];
	onPickerMouseEnter?: () => void;
	onPickerMouseLeave?: () => void;
};

const PickerButton = forwardRef<HTMLButtonElement, Props>(
	(
		{
			onClick,
			isLoading = false,
			isSelected = false,
			customIcon,
			isInBetweenColumnPickerEnabled,
			onPickerMouseEnter,
			onPickerMouseLeave,
		}: Props,
		ref,
	) => {
		const { formatMessage } = useIntl();

		const tooltipContent = formatMessage(
			isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')
				? messages.addColumnTooltipText
				: messages.tooltipText,
		);
		const tooltipOptions: IconButtonProps['tooltip'] = {
			position: 'bottom',
			content: tooltipContent,
			delay: 0,
		};

		const isDisabled = !isSelected && isLoading;

		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleOnClick = useCallback(
			(event: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'button',
					}),
					'ninColumnPickerButton',
					{
						isInline: !!isInBetweenColumnPickerEnabled,
					},
				);
				onClick?.(event, analyticsEvent);
			},
			[createAnalyticsEvent, isInBetweenColumnPickerEnabled, onClick],
		);

		if (isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')) {
			return (
				<Tooltip position="top" content={tooltipContent} delay={0}>
					<Pressable
						xcss={pressableStyles}
						testId="issue-table-column-picker.ui.column-picker.picker-button.pressable-add-column"
						isDisabled={isDisabled}
						onClick={handleOnClick}
						onMouseEnter={onPickerMouseEnter}
						onMouseLeave={onPickerMouseLeave}
						ref={ref}
						aria-haspopup="true"
						aria-expanded={isSelected}
					>
						<AddIcon label={tooltipContent} />
					</Pressable>
				</Tooltip>
			);
		}
		const icon =
			customIcon ?? (fg('jira_inline_field_config_gate') ? TableColumnsDistributeIcon : AddIcon);

		return (
			<IconButton
				icon={icon}
				testId="issue-table-column-picker.ui.column-picker.picker-button.picker-button"
				label={tooltipContent}
				appearance="subtle"
				isDisabled={isDisabled}
				onClick={fg('jira_inline_field_config_gate') ? handleOnClick : onClick}
				ref={ref}
				aria-haspopup="true"
				aria-expanded={isSelected}
				isSelected={isSelected}
				tooltip={tooltipOptions}
				isTooltipDisabled={false}
				{...(fg('relay_migration_children_issues_section') ? { spacing: 'compact' } : {})}
			/>
		);
	},
);

export const PickerButtonFallback = ({
	customIcon,
	isInBetweenColumnPickerEnabled,
}: {
	customIcon?: IconProp;
	isInBetweenColumnPickerEnabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const tooltipContent = formatMessage(
		isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')
			? messages.addColumnTooltipText
			: messages.tooltipText,
	);

	if (isInBetweenColumnPickerEnabled && fg('jira_inline_field_config_gate')) {
		return (
			<Tooltip position="top" content={tooltipContent} delay={0}>
				<Pressable xcss={pressableStyles} isDisabled>
					<AddIcon label={tooltipContent} />
				</Pressable>
			</Tooltip>
		);
	}

	const icon =
		customIcon ?? (fg('jira_inline_field_config_gate') ? TableColumnsDistributeIcon : AddIcon);
	return <IconButton icon={icon} label={tooltipContent} appearance="subtle" isDisabled />;
};

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '24px',
	width: '24px',
	borderRadius: token('radius.small'),
	position: 'relative',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	boxShadow: 'elevation.shadow.overlay',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

export default PickerButton;
